import { action, observable } from 'mobx';
import { HttpClient } from 'Mri.Web.Common';

export interface PodStatus {
    name: string;
    status: string;
    ready: boolean;
    restartCount: number;
    startTime: string | null;
    app: string | null;
    image: string | null;
    runningImage: string | null;
    imageId: string | null;
    imageDrift: boolean;
    node: string | null;
    restartTime: string | null;
    restartReason: string | null;
}

export interface ClearOrleansResult {
    table: string;
    rowsAffected: number;
}

export interface ActionTargets {
    kubernetesApi: string | null;
    kubernetesIdentity: string | null;
    sqlServer: string | null;
    sqlIdentity: string | null;
}

/** What scaling down one pod would do — resolved server-side so the confirmation shows real numbers. */
export interface PodScaleDownPlan {
    podName: string;
    app: string | null;
    node: string | null;
    deployment: string | null;
    ownerKind: string;
    currentReplicas: number;
    targetReplicas: number;
    canScaleDown: boolean;
    blockedReason: string | null;
    lastReplica: boolean;
    commands: string[];
}

export interface PodScaleDownResult {
    podName: string;
    deployment: string;
    previousReplicas: number;
    newReplicas: number;
}

export interface LiveManifestDocument {
    kind: string;
    name: string;
    yaml: string;
}

export interface LiveManifest {
    readAtUtc: string;
    namespace: string;
    documents: LiveManifestDocument[];
    /** The fixed set of kinds this view reads. Nothing outside it is shown, deployed or not. */
    includedKinds: string[];
    /** Kinds deliberately omitted, with the reason - so absence never reads as "the cluster has none". */
    excludedKinds: string[];
    /** Kinds that should have been here but could not be read, usually a missing permission. */
    unreadableKinds: string[];
}

export interface CrashDumpFile {
    name: string;
    sizeBytes: number;
    modifiedUtc: string;
    /** 'crash' = runtime crash dump (large), 'heap' = gcdump (small). */
    kind: string;
}

export interface CrashDumps {
    /** Null when this pod's node could not be determined - the page must not invent one. */
    nodeName: string | null;
    hostPath: string;
    directoryReadable: boolean;
    unreadableReason: string | null;
    files: CrashDumpFile[];
    /** Nodes whose dumps this pod cannot see - dumps are node-local and internal runs on one node. */
    unreadableNodes: string[];
}

/** A silohost pod Overreaction Mode currently counts as crashed. */
export interface CrashedSilo {
    podName: string;
    reason: string | null;
    crashedSinceUtc: string;
    /** True once it has stayed crashed long enough to trigger a recovery. */
    unrecovered: boolean;
}

/** One moment a silohost pod was flagged as crashed-and-not-recovering - recorded with the mode on or off. */
export interface OverreactionDetection {
    detectedUtc: string;
    crashedSinceUtc: string | null;
    pods: string[];
    modeEnabled: boolean;
    outcome: string;
    detectedBy: string | null;
}

export interface OverreactionRecovery {
    startedUtc: string;
    finishedUtc: string | null;
    /** 'Succeeded' or 'Failed'. */
    outcome: string | null;
    actedBy: string | null;
    triggerPods: string[];
    steps: string[];
    error: string | null;
}

/** Overreaction Mode (#170) - mirrors OverreactionModeStatusDto in Services/OverreactionModeDto.cs. */
export interface OverreactionModeStatus {
    enabled: boolean;
    /** Off, Watching, Crashed, Recovering, CoolingDown or BreakerTripped. */
    state: string;
    stateDetail: string;
    changedBy: string | null;
    changedUtc: string | null;
    leaderPod: string | null;
    servedByPod: string | null;
    lastCheckUtc: string | null;
    lastCheckError: string | null;
    crashedSilos: CrashedSilo[];
    /** Set whenever a silohost pod has crossed the won't-recover line - on or off. */
    notRecoveringSinceUtc: string | null;
    /** Newest first. */
    detections: OverreactionDetection[];
    lastRecovery: OverreactionRecovery | null;
    recoveriesInWindow: number;
    maxRecoveriesPerWindow: number;
    breakerWindowHours: number;
    cooldownMinutes: number;
    unrecoveredAfterMinutes: number;
    cooldownUntilUtc: string | null;
    breakerTrippedUtc: string | null;
    breakerReason: string | null;
    /** What turning it on means - served by the API so the page and the watchdog can't drift apart. */
    implications: string[];
}

export interface SqlHealth {
    available: boolean;
    unavailableReason: string | null;
    serverName: string | null;
    cpuCount: number;
    /** The EFFECTIVE ceiling. With the configured value at 0 SQL derives it from cpuCount. */
    maxWorkerThreads: number;
    /** The CONFIGURED setting; 0 means "auto", which is the usual case. */
    configuredMaxWorkerThreads: number;
    currentWorkers: number;
    activeWorkers: number;
    /** Every wait total below is cumulative since startup, so it is meaningless without this. */
    uptimeHours: number;
    threadPoolWaits: number;
    threadPoolWaitHours: number;
    resourceSemaphoreWaits: number;
    resourceSemaphoreWaitHours: number;
}

export class InfrastructureStore {
    public constructor(httpClient: HttpClient) {
        this.httpClient = httpClient;
    }

    @observable
    public pods: PodStatus[] = [];

    @observable
    public loading = false;

    @observable
    public actionInProgress = false;

    @observable
    public lastClearOrleansResults: ClearOrleansResult[] = [];

    @observable
    public targets: ActionTargets = { kubernetesApi: null, kubernetesIdentity: null, sqlServer: null, sqlIdentity: null };

    @observable
    public podScaleDownPlan: PodScaleDownPlan | null = null;

    @observable
    public podPlanLoading = false;

    @observable
    public lastPodScaleDownResult: PodScaleDownResult | null = null;

    /** Surfaced on the page rather than thrown away — a per-pod scale-down can be refused server-side. */
    @observable
    public lastPodActionError: string | null = null;

    @observable
    public liveManifest: LiveManifest | null = null;

    @observable
    public liveManifestLoading = false;

    @observable
    public crashDumps: CrashDumps | null = null;

    @observable
    public overreactionMode: OverreactionModeStatus | null = null;

    @observable
    public overreactionModeSaving = false;

    @observable
    public overreactionModeError: string | null = null;

    @action
    public async loadOverreactionModeAsync() {
        try {
            this.overreactionMode =
                await this.httpClient.get<OverreactionModeStatus>('/api/v1/infrastructure/overreaction-mode') ?? null;
        } catch (e) {
            this.overreactionModeError = InfrastructureStore.describeError(e);
        }
    }

    @action
    public async setOverreactionModeAsync(enabled: boolean) {
        this.overreactionModeSaving = true;
        this.overreactionModeError = null;
        try {
            this.overreactionMode = await this.httpClient.put<OverreactionModeStatus>(
                '/api/v1/infrastructure/overreaction-mode',
                { enabled }
            ) ?? this.overreactionMode;
        } catch (e) {
            this.overreactionModeError = InfrastructureStore.describeError(e);
        } finally {
            this.overreactionModeSaving = false;
        }
    }

    @observable
    public sqlHealth: SqlHealth | null = null;

    @observable
    public sqlHealthLoading = false;

    @action
    public async loadPodsAsync() {
        this.loading = true;
        try {
            this.pods = await this.httpClient.get<PodStatus[]>('/api/v1/infrastructure/pods') ?? [];
        } finally {
            this.loading = false;
        }
    }

    /** Deliberately NOT called on mount: it is a heavier read and only wanted when the section is opened. */
    @action
    public async loadLiveManifestAsync() {
        this.liveManifestLoading = true;
        try {
            this.liveManifest =
                await this.httpClient.get<LiveManifest>('/api/v1/infrastructure/live-manifest') ?? null;
        } finally {
            this.liveManifestLoading = false;
        }
    }

    @action
    public async loadCrashDumpsAsync() {
        this.crashDumps = await this.httpClient.get<CrashDumps>('/api/v1/infrastructure/crash-dumps') ?? null;
    }

    /**
     * Read live every time - a stale worker-thread count is worse than none, because the whole point
     * of the panel is to show pressure as it happens.
     */
    @action
    public async loadSqlHealthAsync() {
        this.sqlHealthLoading = true;
        try {
            this.sqlHealth = await this.httpClient.get<SqlHealth>('/api/v1/infrastructure/sql-health') ?? null;
        } finally {
            this.sqlHealthLoading = false;
        }
    }

    @action
    public async loadTargetsAsync() {
        const t = await this.httpClient.get<ActionTargets>('/api/v1/infrastructure/targets');
        if (t) this.targets = t;
    }

    @action
    public async scaleDownAllAsync() {
        this.actionInProgress = true;
        try {
            await this.httpClient.post('/api/v1/infrastructure/scale-down');
            await this.loadPodsAsync();
        } finally {
            this.actionInProgress = false;
        }
    }

    @action
    public async scaleUpAllAsync() {
        this.actionInProgress = true;
        try {
            await this.httpClient.post('/api/v1/infrastructure/scale-up');
            await this.loadPodsAsync();
        } finally {
            this.actionInProgress = false;
        }
    }

    /** Loads the plan for one pod. Returns false when it couldn't be resolved, so the caller
     *  can leave the confirmation closed instead of showing a modal with nothing in it. */
    @action
    public async loadPodScaleDownPlanAsync(podName: string): Promise<boolean> {
        this.podPlanLoading = true;
        this.lastPodActionError = null;
        this.lastPodScaleDownResult = null;
        this.podScaleDownPlan = null;
        try {
            this.podScaleDownPlan = await this.httpClient.get<PodScaleDownPlan>(
                `/api/v1/infrastructure/pods/${encodeURIComponent(podName)}/scale-down-plan`
            ) ?? null;
            return this.podScaleDownPlan !== null;
        } catch (e) {
            this.lastPodActionError = InfrastructureStore.describeError(e);
            return false;
        } finally {
            this.podPlanLoading = false;
        }
    }

    @action
    public async scaleDownPodAsync(podName: string) {
        this.actionInProgress = true;
        this.lastPodActionError = null;
        try {
            this.lastPodScaleDownResult = await this.httpClient.post<PodScaleDownResult>(
                `/api/v1/infrastructure/pods/${encodeURIComponent(podName)}/scale-down`
            ) ?? null;
        } catch (e) {
            this.lastPodActionError = InfrastructureStore.describeError(e);
        } finally {
            this.actionInProgress = false;
        }

        // Refresh either way — on a refusal the list is still the operator's ground truth. Kept out of
        // the try above so a failed refresh isn't reported as a failed scale-down.
        await this.loadPodsAsync();
    }

    @action
    public async clearOrleansMembershipAsync() {
        this.actionInProgress = true;
        try {
            this.lastClearOrleansResults = await this.httpClient.post<ClearOrleansResult[]>('/api/v1/infrastructure/clear-orleans') ?? [];
        } finally {
            this.actionInProgress = false;
        }
    }

    // HttpClient rejects with { message, body } where body is the server's Error payload, so prefer the
    // server's own wording — it's the one that explains why an action was refused.
    private static describeError(e: any): string {
        return e?.body?.message ?? e?.message ?? 'Unexpected error';
    }

    private readonly httpClient: HttpClient;
}
